import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Reveal } from '../../shared/ui';

export interface LegalSectionMeta {
  id: string;
  title: string;
}

interface LegalLayoutProps {
  title: string;
  effective: string;
  // Opening paragraph(s), shown above the first section.
  intro: ReactNode;
  sections: LegalSectionMeta[];
  other: { to: string; label: string };
  children: ReactNode;
}

const pill = 'rounded-full border-2 border-[#2c2c2e] px-4 py-2 text-sm text-[#d2d2d7]';

export function LegalSection({ id, num, title, children }: { id: string; num: number; title: string; children: ReactNode }) {
  return (
    <Reveal as="section" id={id} className="scroll-mt-24 rounded-[28px] bg-[#161617] p-[clamp(28px,4vw,44px)]">
      <div className="mb-4 flex items-baseline gap-4">
        <span className="text-[15px] font-bold tabular-nums text-accent">{String(num).padStart(2, '0')}</span>
        <h2 className="m-0 text-[clamp(24px,2.6vw,30px)] font-bold leading-[1.15] tracking-[-0.03em]">{title}</h2>
      </div>
      <div className="flex flex-col">{children}</div>
    </Reveal>
  );
}

export function LegalLayout({ title, effective, intro, sections, other, children }: LegalLayoutProps) {
  const [active, setActive] = useState(sections[0]?.id);

  // Highlight the section currently under the header in the "On this page" list.
  useEffect(() => {
    const onScroll = () => {
      let cur = sections[0]?.id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top < 160) cur = s.id;
      }
      setActive(cur);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  const goTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="bg-black text-[#f5f5f7]">
      <div className="mx-auto w-full max-w-[1200px] px-6 pt-[clamp(48px,9vw,88px)]">
        <Reveal immediate className="mb-2.5 text-[17px] font-semibold text-accent">Legal</Reveal>
        <Reveal as="h1" kind="blur" immediate delay={100} className="m-0 text-[clamp(44px,7vw,80px)] font-bold leading-[1.02] tracking-[-0.045em]">
          {title}
        </Reveal>
        <Reveal immediate delay={400} className="mt-6 flex flex-wrap gap-2.5">
          <span className={pill}>Effective {effective}</span>
          <span className={pill}>Governed by the laws of Jamaica</span>
        </Reveal>
      </div>

      <div className="mx-auto flex w-full max-w-[1200px] flex-wrap gap-14 px-6 pb-[clamp(72px,10vw,120px)] pt-[clamp(40px,7vw,72px)]">
        <aside className="hidden max-w-[280px] flex-[1_1_220px] lg:block">
          <nav aria-label="Table of contents" className="sticky top-24 flex flex-col gap-0.5">
            <div className="px-4 pb-2.5 text-[13px] text-[#86868b]">On this page</div>
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => goTo(s.id)}
                className={`rounded-full px-4 py-[9px] text-left text-[15px] transition-colors ${
                  s.id === active ? 'bg-[#1c1c1e] font-semibold text-[#f5f5f7]' : 'text-[#86868b] hover:text-[#d2d2d7]'
                }`}
              >
                {s.title}
              </button>
            ))}
          </nav>
        </aside>

        <article className="flex min-w-0 max-w-[720px] flex-[999_1_300px] flex-col gap-5">
          <div className="text-[17px] leading-[1.65] text-[#c7c7cc] [text-wrap:pretty]">{intro}</div>
          {children}

          <Reveal className="mt-5 flex flex-wrap items-center justify-between gap-5 rounded-[28px] bg-[#f5f5f7] p-[clamp(28px,4vw,44px)] text-[#1d1d1f]">
            <div>
              <div className="text-2xl font-bold tracking-[-0.03em]">Questions about this policy?</div>
              <div className="mt-1 text-[#6e6e73]">rtequipment.co.ltd@gmail.com · +1 876 422 5370</div>
            </div>
            <a
              href="mailto:rtequipment.co.ltd@gmail.com"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1d1d1f] px-[22px] py-3 font-semibold text-white transition-all hover:scale-105 hover:bg-black"
            >
              Email us <ChevronRight size={16} strokeWidth={2.5} />
            </a>
          </Reveal>

          <Link to={other.to} className="mt-2 inline-flex items-center gap-1 self-start py-2 text-[17px] text-accent hover:underline">
            {other.label} <ChevronRight size={16} strokeWidth={2.5} />
          </Link>
        </article>
      </div>
    </div>
  );
}
